import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useGoals } from '../contexts/GoalContext';
import { useOKR } from '../contexts/OKRContext';
import { usePerformance } from '../contexts/PerformanceContext';
import { motion } from 'framer-motion';

const AchievementBadges = () => {
  const { user } = useAuth();
  const { analytics: goalAnalytics } = useGoals();
  const { analytics: okrAnalytics } = useOKR();
  const { analytics: performanceAnalytics } = usePerformance();
  
  const [achievements, setAchievements] = useState([]);
  const [streaks, setStreaks] = useState({
    currentStreak: 0,
    longestStreak: 0,
    lastCheckIn: null
  });

  // Define achievement criteria
  const achievementCriteria = [
    {
      id: 'first_goal',
      title: 'Goal Setter',
      description: 'Created your first goal',
      icon: '🎯',
      color: 'bg-blue-100 text-blue-800',
      threshold: 1,
      metric: 'total_goals',
      source: 'goals'
    },
    {
      id: 'goal_achiever',
      title: 'Goal Achiever',
      description: 'Completed 5 goals',
      icon: '🏆',
      color: 'bg-green-100 text-green-800',
      threshold: 5,
      metric: 'completed_goals',
      source: 'goals'
    },
    {
      id: 'goal_master',
      title: 'Goal Master',
      description: 'Completed 20 goals',
      icon: '👑',
      color: 'bg-yellow-100 text-yellow-800',
      threshold: 20,
      metric: 'completed_goals',
      source: 'goals'
    },
    {
      id: 'okr_champion',
      title: 'OKR Champion',
      description: 'Achieved 90%+ OKR score',
      icon: '🚀',
      color: 'bg-indigo-100 text-indigo-800',
      threshold: 0.9,
      metric: 'avg_score',
      source: 'okr'
    },
    {
      id: 'perfectionist',
      title: 'Perfectionist',
      description: '100% goal completion rate',
      icon: '💎',
      color: 'bg-purple-100 text-purple-800',
      threshold: 100,
      metric: 'completion_rate',
      source: 'goals'
    },
    {
      id: 'consistent_performer',
      title: 'Consistent Performer',
      description: 'Maintained 80%+ performance for 3 months',
      icon: '📈',
      color: 'bg-emerald-100 text-emerald-800',
      threshold: 0.8,
      metric: 'avg_overall_score',
      source: 'performance'
    },
    {
      id: 'early_bird',
      title: 'Early Bird',
      description: 'Completed goals before deadline',
      icon: '🌅',
      color: 'bg-orange-100 text-orange-800',
      threshold: 3,
      metric: 'early_completions',
      source: 'goals'
    },
    {
      id: 'team_player',
      title: 'Team Player',
      description: 'Completed 10 team-level objectives',
      icon: '👥',
      color: 'bg-cyan-100 text-cyan-800',
      threshold: 10,
      metric: 'team_objectives',
      source: 'okr'
    },
    {
      id: 'feedback_master',
      title: 'Feedback Master',
      description: 'Received 20+ peer feedback ratings',
      icon: '💬',
      color: 'bg-pink-100 text-pink-800',
      threshold: 20,
      metric: 'feedback_count',
      source: 'performance'
    },
    {
      id: 'streak_warrior',
      title: 'Streak Warrior',
      description: '30-day check-in streak',
      icon: '🔥',
      color: 'bg-red-100 text-red-800',
      threshold: 30,
      metric: 'current_streak',
      source: 'streak'
    }
  ];

  // Calculate achievements based on current data
  useEffect(() => {
    const calculateAchievements = () => {
      const earnedAchievements = [];
      
      achievementCriteria.forEach(criteria => {
        let currentValue = 0;
        
        switch (criteria.source) {
          case 'goals':
            currentValue = goalAnalytics?.[criteria.metric] || 0;
            break;
          case 'okr':
            currentValue = okrAnalytics?.[criteria.metric] || 0;
            break;
          case 'performance':
            currentValue = performanceAnalytics?.[criteria.metric] || 0;
            break;
          case 'streak':
            currentValue = streaks[criteria.metric] || 0;
            break;
          default:
            currentValue = 0;
        }

        const isEarned = currentValue >= criteria.threshold;
        const progress = Math.min((currentValue / criteria.threshold) * 100, 100);

        earnedAchievements.push({
          ...criteria,
          isEarned,
          currentValue,
          progress,
          earnedDate: isEarned ? new Date() : null
        });
      });

      setAchievements(earnedAchievements);
    };

    calculateAchievements();
  }, [goalAnalytics, okrAnalytics, performanceAnalytics, streaks]);

  // Simulate streak calculation
  useEffect(() => {
    // This would normally be calculated from actual check-in data
    const simulateStreaks = () => {
      const today = new Date();
      const daysSinceLastCheckIn = Math.floor(Math.random() * 3); // 0-2 days
      const currentStreak = Math.floor(Math.random() * 45); // 0-45 days
      const longestStreak = Math.max(currentStreak, Math.floor(Math.random() * 60));

      setStreaks({
        currentStreak,
        longestStreak,
        lastCheckIn: new Date(today.getTime() - daysSinceLastCheckIn * 24 * 60 * 60 * 1000)
      });
    };

    simulateStreaks();
  }, []);

  const earnedAchievements = achievements.filter(a => a.isEarned);
  const inProgressAchievements = achievements.filter(a => !a.isEarned && a.progress > 0);

  return (
    <div className="space-y-6">
      {/* Earned Achievements */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Achievement Badges</h3>
              <p className="text-sm text-gray-600">
                {earnedAchievements.length} of {achievements.length} achievements unlocked
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">{earnedAchievements.length}</div>
              <div className="text-sm text-gray-500">Earned</div>
            </div>
          </div>
        </div>

        <div className="p-6">
          {earnedAchievements.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {earnedAchievements.map((achievement) => (
                <motion.div
                  key={achievement.id}
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="relative group"
                >
                  <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-2 border-yellow-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center space-x-3">
                      <div className="text-3xl">{achievement.icon}</div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{achievement.title}</h4>
                        <p className="text-sm text-gray-600">{achievement.description}</p>
                        <div className="mt-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${achievement.color}`}>
                            Earned
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 text-4xl mb-2">🏆</div>
              <p className="text-gray-500">No achievements earned yet. Keep working to unlock your first badge!</p>
            </div>
          )}
        </div>
      </div>

      {/* Progress Towards Next Achievements */}
      {inProgressAchievements.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Progress Towards Next Achievements</h3>
            <p className="text-sm text-gray-600">You're making progress on these achievements</p>
          </div>

          <div className="p-6">
            <div className="space-y-4">
              {inProgressAchievements.slice(0, 5).map((achievement) => (
                <div key={achievement.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl opacity-50">{achievement.icon}</div>
                      <div>
                        <h4 className="font-medium text-gray-900">{achievement.title}</h4>
                        <p className="text-sm text-gray-600">{achievement.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900">
                        {achievement.currentValue} / {achievement.threshold}
                      </div>
                      <div className="text-xs text-gray-500">
                        {Math.round(achievement.progress)}%
                      </div>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${achievement.progress}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Streak Information */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Consistency Tracking</h3>
          <p className="text-sm text-gray-600">Your check-in streaks and consistency</p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl mb-2">🔥</div>
              <div className="text-2xl font-bold text-orange-600">{streaks.currentStreak}</div>
              <div className="text-sm text-gray-600">Current Streak</div>
              <div className="text-xs text-gray-500 mt-1">days</div>
            </div>
            
            <div className="text-center">
              <div className="text-3xl mb-2">📅</div>
              <div className="text-2xl font-bold text-blue-600">{streaks.longestStreak}</div>
              <div className="text-sm text-gray-600">Longest Streak</div>
              <div className="text-xs text-gray-500 mt-1">days</div>
            </div>
            
            <div className="text-center">
              <div className="text-3xl mb-2">⏰</div>
              <div className="text-2xl font-bold text-green-600">
                {streaks.lastCheckIn ? new Date(streaks.lastCheckIn).toLocaleDateString() : 'Never'}
              </div>
              <div className="text-sm text-gray-600">Last Check-in</div>
              <div className="text-xs text-gray-500 mt-1">date</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AchievementBadges;